import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import ReactDOM from 'react-dom';
import LocationSelectDropdown from '@components/framework/locationselect/LocationSelectDropdown.tsx';
import SvgChevronThin from '@icons/ChevronThin.tsx';
import SvgChevronThinUp from '@icons/ChevronThinUp.tsx';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { CGV2LocationOutDTO, CGV2SmallCompanyOutDTO, LocationContactTypeEnum } from '@api/ringil-api.v2.tsx';
import { isNotBlank } from '@utils/utils.tsx';
import { Z_LAYERS } from '@styles/zLayers.ts';
import { getOverlayZIndex } from '@utils/contentBounds.ts';
import COText from '@app/pages/COText/COText.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';

interface ComponentProps {
  value: string;
  onChange: (companyLocation: CGV2LocationOutDTO) => void;
  fullWidth?: boolean;
  dataTestId?: string;
}

const LocationSelect: React.FC<ComponentProps> = ({ value, onChange, fullWidth = false, dataTestId }) => {
  const { getCGLocationsV2, getOneCGLocationV2 } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [isLoading, setIsLoading] = useState(false);
  const [loadedValue, setLoadedValue] = useState<CGV2LocationOutDTO>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; zIndex: number }>({
    top: 0,
    left: 0,
    width: 0,
    zIndex: Z_LAYERS.popup,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [locations, setLocations] = useState<CGV2SmallCompanyOutDTO[]>([]);
  const [locationOpen, setLocationOpen] = useState(false);

  // Zavření dropdownu při kliknutí mimo
  useEffect(() => {
    load('');
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInDropdown = dropdownRef.current?.contains(target);
      const clickedInTrigger = buttonRef.current?.contains(target);
      if (!clickedInDropdown && !clickedInTrigger) {
        setIsOpen(false);
      }
    };

    const handleScroll =  (event: Event) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    if (isNotBlank(value)) {
      getOneCGLocationV2(value, data => {
        setLoadedValue(data);
      });
    }
  }, [value]);

  const load = (query: string) => {
    getCGLocationsV2({ query }, data => {
      setLocations(data);
    });

    if (isNotBlank(query)) {
      setLocationOpen(true);
    } else {
      setLocationOpen(false);
    }
  };

  // Funkce pro otevření dropdownu a nastavení jeho pozice
  const handleToggle = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        // One step above the dialog/side panel this select lives in, when PrimeReact pushed that panel
        // above the popup layer (see getOverlayZIndex); the plain popup layer everywhere else.
        zIndex: getOverlayZIndex(buttonRef.current),
      });
    }
    setIsOpen(!isOpen);
  };

  const getBadge = (locationContactType: LocationContactTypeEnum) => {
    switch (locationContactType) {
      case LocationContactTypeEnum.OUR_LOCATION:
        return (
          <COBadge type={'processing'} variant={'outlined'}
                   className={'min-w-[60px] w-[25%] max-w-max inline-block'}>
            <COText label={tr('LocationSelect.naseLokace', 'Naše lokace')}
                    className={'overflow-hidden text-ellipsis whitespace-nowrap'} />
          </COBadge>
        );
      case LocationContactTypeEnum.OUR_PARTNER_LOCATION:
        return (
          <COBadge type={'processing'} variant={'outlined'}
                   className={'min-w-[60px] w-[25%] max-w-max inline-block'}>
            <COText label={tr('LocationSelect.ourPartnerLocation', 'Partnerská lokace')}
                    className={'overflow-hidden text-ellipsis whitespace-nowrap'} />
          </COBadge>
        );
      case LocationContactTypeEnum.OUR_EXTERNAL_LOCATION:
        return (
          <COBadge type={'draft'} variant={'outlined'}
                   className={'min-w-[60px] w-[25%] max-w-max inline-block'}>
            <COText label={tr('LocationSelect.externi', 'Externí')}
                    className={'overflow-hidden text-ellipsis whitespace-nowrap'} />
          </COBadge>
        );
      default:
        return (
          <COBadge type={'draft'} variant={'outlined'}
                   className={'min-w-[60px] w-[25%] max-w-max inline-block'}>
            <COText label={tr('LocationSelect.partner', 'Partner')}
                    className={'overflow-hidden text-ellipsis whitespace-nowrap'} />
          </COBadge>
        );
    }
  };

  const findAndFormatValue = () => {
    if (loadedValue == null) return <div></div>;

    return (
      <>
        <div className={'overflow-hidden text-ellipsis whitespace-nowrap w-[70%]'}>
          {loadedValue.locationName} | {loadedValue.companyName}
        </div>

        {getBadge(loadedValue.locationContactType)}
      </>
    );
  };

  const onSelectHandler = (companyLocationId: string) => {
    setIsOpen(false);
    getOneCGLocationV2(companyLocationId, data => {
      onChange(data);
    });
  };

  return (
    <>
      <div
        ref={buttonRef}
        className={twMerge(
          // Explicit white background: on a grey panel (RingilSidebar) the transparent trigger looked disabled.
          'flex flex-row px-2 h-[40px] justify-between bg-white border border-solid border-content-secondary items-center cursor-pointer text-content-secondary p-inputwrapper',
          clsx({ 'w-full': fullWidth }),
        )}
        onClick={handleToggle}
        data-testid={dataTestId}
      >
        <div className={'flex flex-row justify-between items-center w-full'}>
          {findAndFormatValue()}
          {isOpen ? <SvgChevronThinUp /> : <SvgChevronThin />}
        </div>
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: dropdownPosition.zIndex,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <LocationSelectDropdown
              isLoading={isLoading}
              companyLocations={locations}
              dataTestIdBase={dataTestId}
              onFilter={v => load(v)}
              onSelect={(companyLocationId: string) => {
                onSelectHandler(companyLocationId);
              }}
              locationOpen={locationOpen}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default LocationSelect;
